/**
 * DropZone
 *
 * Reusable drag-and-drop file target. Calls `onFiles` for any files
 * dropped or selected via a hidden file input opened when the body is
 * clicked. Renders its children + a highlight overlay during dragover.
 *
 * Originally extracted from `pages/knowledge/DocumentUpload.tsx`; used
 * by the AUI Files component for VFS uploads, and intended for the
 * Knowledge Base upload flow to adopt next.
 */

import { useCallback, useRef, useState, type ReactNode, type DragEvent, type ChangeEvent } from 'react';
import { cn } from '@/lib/cn';

export interface DropZoneProps {
  onFiles: (files: File[]) => void;
  /** Comma-separated `accept` attribute for the underlying input. */
  accept?: string;
  /** Allow multiple files in a single drop / file-picker selection. */
  multiple?: boolean;
  disabled?: boolean;
  /** Optional class added to the outer wrapper. */
  className?: string;
  /**
   * Click anywhere in the wrapper opens the file picker. Default `true`.
   * Set `false` when the zone covers an area that has its own click
   * semantics (e.g. a chat thread where clicks select text / focus the
   * composer). The picker is then only reachable via an external `+`
   * button (which can use this component or its own hidden input).
   */
  openOnClick?: boolean;
  /**
   * Optional render override. Receives the current dragging state so the
   * caller can style highlights inline; if omitted, the wrapper renders
   * children with a 2px dashed border that turns primary on hover.
   */
  children?: ReactNode | ((state: { isDragging: boolean }) => ReactNode);
}

export function DropZone({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  openOnClick = true,
  className,
  children,
}: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);

  const handleDragEnter = useCallback((e: DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, [disabled]);

  const handleDragOver = useCallback((e: DragEvent) => {
    if (disabled) return;
    e.preventDefault();
  }, [disabled]);

  const handleDragLeave = useCallback((e: DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, [disabled]);

  const handleDrop = useCallback((e: DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFiles(multiple ? files : [files[0]]);
  }, [disabled, multiple, onFiles]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) onFiles(multiple ? files : [files[0]]);
    // Clear so selecting the same file twice still fires.
    e.target.value = '';
  }, [multiple, onFiles]);

  const handleClick = useCallback(() => {
    if (disabled || !openOnClick) return;
    inputRef.current?.click();
  }, [disabled, openOnClick]);

  const body = typeof children === 'function' ? children({ isDragging }) : children;

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      className={cn(
        'relative',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
      />
      {body}
    </div>
  );
}
